import React from "react";
import SummaryCard from "./SummaryCard";
import { getComplianceNameById, getOutletFilterName, getAtuPbtName, renderYesNoBadge, getLegendValue, getTankMaterialName } from "./summaryHelpers";
import { TankInstallationInput, OutletFilterData, AtuPbtData, ComplianceData, LegendData, TankMaterialData } from "../TankInstallationForm.types";

type Props = {
    data?: TankInstallationInput;
    complianceData?: ComplianceData[];
    outletFilterData?: OutletFilterData[];
    atuPbtData?: AtuPbtData[];
    legendData?: LegendData[];
    tankMaterialData?: TankMaterialData[];
};

const TankInstallationReadOnly: React.FC<Props> = ({ data, complianceData, outletFilterData, atuPbtData, legendData, tankMaterialData }) => {
    if (!data) return null;

    // Helper function to check if Tank 2 has meaningful data
    const hasTank2Data = (data: any) => {
        return (
            // Check for actual numeric values greater than 0
            (data?.tankSize2 && data.tankSize2 > 0) ||
            (data?.tank2DepthToLidInches && data.tank2DepthToLidInches > 0) ||
            // Check for meaningful text values
            (data?.tankSize2Units && data.tankSize2Units.trim() !== '') ||
            (data?.tank2MaterialOtherDescription && data.tank2MaterialOtherDescription.trim() !== '') ||
            // Check for ID selections (greater than 0, not just truthy)
            (data?.tankMaterialId2 && data.tankMaterialId2 > 0) ||
            (data?.legendId2 && data.legendId2 > 0) ||
            (data?.tank2OutletFilterId && data.tank2OutletFilterId > 0) ||
            (data?.tank2AtuPbtId && data.tank2AtuPbtId > 0) ||
            // Check for compliance IDs that aren't just default values
            (data?.tank2ComplianceId && data.tank2ComplianceId > 0) ||
            (data?.tank2MaterialComplianceId && data.tank2MaterialComplianceId > 0) ||
            (data?.tank2LegendComplianceId && data.tank2LegendComplianceId > 0) ||
            (data?.tank2OutletDeviceComplianceId && data.tank2OutletDeviceComplianceId > 0) ||
            (data?.tank2MultiChamberedComplianceId && data.tank2MultiChamberedComplianceId > 0) ||
            (data?.tank2OutletFilterComplianceId && data.tank2OutletFilterComplianceId > 0) ||
            (data?.tank2WatertightComplianceId && data.tank2WatertightComplianceId > 0) ||
            (data?.tank2LevelComplianceId && data.tank2LevelComplianceId > 0) ||
            (data?.tank2DepthToLidComplianceId && data.tank2DepthToLidComplianceId > 0) ||
            (data?.tank2AtuPbtComplianceId && data.tank2AtuPbtComplianceId > 0) ||
            // Check for boolean values
            data?.tank2MultiChambered === true ||
            data?.tank2InSeries === true ||
            data?.tank2Risers === true ||
            // Check for comments
            (data?.tank2SizeComment && data.tank2SizeComment.trim() !== '') ||
            (data?.tank2MaterialComment && data.tank2MaterialComment.trim() !== '') ||
            (data?.tank2LegendComment && data.tank2LegendComment.trim() !== '') ||
            (data?.tank2OutletDeviceComment && data.tank2OutletDeviceComment.trim() !== '') ||
            (data?.tank2MultiChamberedComment && data.tank2MultiChamberedComment.trim() !== '') ||
            (data?.tank2OutletFilterComment && data.tank2OutletFilterComment.trim() !== '') ||
            (data?.tank2WatertightComment && data.tank2WatertightComment.trim() !== '') ||
            (data?.tank2LevelComment && data.tank2LevelComment.trim() !== '') ||
            (data?.tank2DepthToLidComment && data.tank2DepthToLidComment.trim() !== '') ||
            (data?.tank2AtuPbtComment && data.tank2AtuPbtComment.trim() !== '')
        );
    };

    const yesNo = (v: any) => {
        if (v === undefined || v === null) return null;
        const b = typeof v === 'boolean' ? v : Number(v) === 1;
        return <span className="print-value">{` (${b ? 'Yes' : 'No'})`}</span>;
    };

    return (
        <>
            {/* Order mirrors form: Size, ATU/PBTS, Material, Inlet/Outlet Device, Multichambered & In Series, Outlet Filter, Legend, Watertight, Level, Depth to Lid & Risers */}
            <SummaryCard
                title={
                    <>
                        <svg className="me-2" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 8h10"/><path d="M7 12h6"/></svg>
                        Tank Installation
                    </>
                }
            >
                {/* Tank Size */}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.tankComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <label>Tank Size</label>
                        {(data as any)?.tankSize1 && (
                            <div className="mt-1">
                                <span className="print-value fw-bold">{`${(data as any)?.tankSize1}${(data as any)?.tankSize1Units ? ` ${data?.tankSize1Units}` : ''}`}</span>
                            </div>
                        )}
                    </div>
                </div>
                {(data as any)?.tankCompliance?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.tankSizeComment}</label>
                    </div>
                ) : null}

                {/* ATU/PBTS */}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.atuPbtComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <label>ATU/PBTS</label>
                        {(data as any)?.atuPbtId && (
                            <div className="mt-1">
                                <span className="print-value fw-bold">{getAtuPbtName(atuPbtData as any, (data as any)?.atuPbtId)}</span>
                            </div>
                        )}
                    </div>
                </div>
                {(data as any)?.atuPbtCompliance?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.atuPbtComment}</label>
                    </div>
                ) : null}

                {/* Tank Material */}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.tankMaterialComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <label>Tank Material</label>
                        {(data as any)?.tankMaterialOtherDescription && (
                            <div className="mt-1">
                                <small className="text-muted">Description: </small>
                                <span className="print-value fw-bold">{(data as any)?.tankMaterialOtherDescription}</span>
                            </div>
                        )}
                        {(data as any)?.tankMaterialId1 && !(data as any)?.tankMaterialOtherDescription && (
                            <div className="mt-1">
                                <span className="print-value fw-bold">{getTankMaterialName(tankMaterialData as any, (data as any)?.tankMaterialId1)}</span>
                            </div>
                        )}
                    </div>
                </div>
                {(data as any)?.tankMaterialCompliance?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.tankMaterialComment}</label>
                    </div>
                ) : null}

                {/* Inlet/Outlet Device */}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.outletDeviceComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <label>Inlet / Outlet Device</label>
                    </div>
                </div>
                {(data as any)?.outletDeviceCompliance?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.outletDeviceComment}</label>
                    </div>
                ) : null}

                {/* Multichambered & In Series */}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.multiChamberedComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <div className="row">
                            <div className="col col-md-6">
                                <label>Is Tank Multichambered?</label>
                                {yesNo((data as any)?.multiChambered)}
                            </div>
                            <div className="col col-md-6">
                                <label>Tanks In Series?</label>
                                {yesNo((data as any)?.inSeries)}
                            </div>
                        </div>
                    </div>
                </div>
                {(data as any)?.multiChamberedCompliance?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.multiChamberedComment}</label>
                    </div>
                ) : null}

                {/* Outlet Filter */}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.outletFilterComplienceId ?? (data as any)?.outletFilterComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <label>Tank Outlet Filter</label>
                        {(data as any)?.outletFilterId && (
                            <div className="mt-1">
                                <span className="print-value fw-bold">{getOutletFilterName(outletFilterData as any, (data as any)?.outletFilterId)}</span>
                            </div>
                        )}
                    </div>
                </div>
                {(data as any)?.outletFilterComplience?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.outletFilterComment}</label>
                    </div>
                ) : null}

                {/* Legend */}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.legendComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <label>Tank Legend</label>
                        {(data as any)?.legendId1 && (
                            <div className="mt-1">
                                <span className="print-value fw-bold">{getLegendValue(legendData as any, (data as any)?.legendId1)}</span>
                            </div>
                        )}
                    </div>
                </div>
                {(data as any)?.legendCompliance?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.legendComment}</label>
                    </div>
                ) : null}

                {/* Watertight */}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.watertightComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <label>Watertight</label>
                    </div>
                </div>
                {(data as any)?.watertightCompliance?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.watertightComment}</label>
                    </div>
                ) : null}

                {/* Level */}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.levelComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <label>Level</label>
                    </div>
                </div>
                {(data as any)?.levelCompliance?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.levelComment}</label>
                    </div>
                ) : null}

                {/* Depth to Lid + Risers */}
                <div className="row mb-3">
                    <div className="col col-md-3">
                        <label>{getComplianceNameById(complianceData as any, (data as any)?.depthToLidComplianceId)}</label>
                    </div>
                    <div className="col col-md-9">
                        <div className="row">
                            <div className="col col-md-6">
                                <label>Depth to Lid</label>
                                {(data as any)?.depthToLidInches !== undefined && (data as any)?.depthToLidInches !== null && (
                                    <div className="mt-1">
                                        <span className="print-value fw-bold">{(data as any)?.depthToLidInches} in</span>
                                    </div>
                                )}
                            </div>
                            <div className="col col-md-6">
                                <label>Riser(s)</label>
                                {yesNo((data as any)?.risers)}
                            </div>
                        </div>
                    </div>
                </div>
                {(data as any)?.depthToLidCompliance?.complianceValue === "Out of Compliance" ? (
                    <div className="row mb-3">
                        <label>{(data as any)?.depthToLidComment}</label>
                    </div>
                ) : null}
            </SummaryCard>

            {((data as any)?.isTank2Showable && hasTank2Data(data as any)) && (
                <SummaryCard
                    title={
                        <>
                            <svg className="me-2" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 8h10"/><path d="M7 12h6"/></svg>
                            Tank 2 Installation
                        </>
                    }
                >
                    {/* Tank 2 Size */}
                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.tank2ComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <label>Tank Size</label>
                            {(data as any)?.tankSize2 && (
                                <div className="mt-1">
                                    <span className="print-value fw-bold">{(data as any)?.tankSize2}{(data as any)?.tankSize2Units ? ` ${data?.tankSize2Units}` : ''}</span>
                                </div>
                            )}
                        </div>
                    </div>
                    {(data as any)?.tank2Compliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.tank2SizeComment}</label>
                        </div>
                    ) : null}

                    {/* Tank 2 ATU/PBTS */}
                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.tank2AtuPbtComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <label>ATU/PBTS</label>
                            {(data as any)?.tank2AtuPbtId && (
                                <div className="mt-1">
                                    <span className="print-value fw-bold">{getAtuPbtName(atuPbtData as any, (data as any)?.tank2AtuPbtId)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                    {(data as any)?.tank2AtuPbtCompliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.tank2AtuPbtComment}</label>
                        </div>
                    ) : null}

                    {/* Tank 2 Material */}
                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.tank2MaterialComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <label>Tank Material</label>
                            {(data as any)?.tank2MaterialOtherDescription && (
                                <div className="mt-1">
                                    <small className="text-muted">Description: </small>
                                    <span className="print-value fw-bold">{(data as any)?.tank2MaterialOtherDescription}</span>
                                </div>
                            )}
                            {(data as any)?.tankMaterialId2 && !(data as any)?.tank2MaterialOtherDescription && (
                                <div className="mt-1">
                                    <span className="print-value fw-bold">{getTankMaterialName(tankMaterialData as any, (data as any)?.tankMaterialId2)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                    {(data as any)?.tank2MaterialCompliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.tank2MaterialComment}</label>
                        </div>
                    ) : null}

                    {/* Tank 2 Inlet/Outlet Device */}
                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.tank2OutletDeviceComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <label>Inlet / Outlet Device</label>
                        </div>
                    </div>
                    {(data as any)?.tank2OutletDeviceCompliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.tank2OutletDeviceComment}</label>
                        </div>
                    ) : null}

                    {/* Tank 2 Multichambered & In Series */}
                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.tank2MultiChamberedComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <div className="row">
                                <div className="col col-md-6">
                                    <label>Is Tank Multichambered?</label>
                                    {yesNo((data as any)?.tank2MultiChambered)}
                                </div>
                                <div className="col col-md-6">
                                    <label>Tanks In Series?</label>
                                    {yesNo((data as any)?.tank2InSeries)}
                                </div>
                            </div>
                        </div>
                    </div>
                    {(data as any)?.tank2MultiChamberedCompliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.tank2MultiChamberedComment}</label>
                        </div>
                    ) : null}

                    {/* Tank 2 Outlet Filter */}
                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.tank2OutletFilterComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <label>Tank Outlet Filter</label>
                            {(data as any)?.tank2OutletFilterId && (
                                <div className="mt-1">
                                    <span className="print-value fw-bold">{getOutletFilterName(outletFilterData as any, (data as any)?.tank2OutletFilterId)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                    {(data as any)?.tank2OutletFilterCompliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.tank2OutletFilterComment}</label>
                        </div>
                    ) : null}

                    {/* Tank 2 Legend */}
                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.tank2LegendComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <label>Tank Legend</label>
                            {(data as any)?.legendId2 && (
                                <div className="mt-1">
                                    <span className="print-value fw-bold">{getLegendValue(legendData as any, (data as any)?.legendId2)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                    {(data as any)?.tank2LegendCompliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.tank2LegendComment}</label>
                        </div>
                    ) : null}

                    {/* Tank 2 Watertight */}
                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.tank2WatertightComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <label>Watertight</label>
                        </div>
                    </div>
                    {(data as any)?.tank2WatertightCompliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.tank2WatertightComment}</label>
                        </div>
                    ) : null}

                    {/* Tank 2 Level */}
                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.tank2LevelComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <label>Level</label>
                        </div>
                    </div>
                    {(data as any)?.tank2LevelCompliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.tank2LevelComment}</label>
                        </div>
                    ) : null}

                    {/* Tank 2 Depth to Lid + Risers */}
                    <div className="row mb-3">
                        <div className="col col-md-3">
                            <label>{getComplianceNameById(complianceData as any, (data as any)?.tank2DepthToLidComplianceId)}</label>
                        </div>
                        <div className="col col-md-9">
                            <div className="row">
                                <div className="col col-md-6">
                                    <label>Depth to Lid</label>
                                    {(data as any)?.tank2DepthToLidInches !== undefined && (data as any)?.tank2DepthToLidInches !== null && (
                                        <div className="mt-1">
                                            <span className="print-value fw-bold">{(data as any)?.tank2DepthToLidInches} in</span>
                                        </div>
                                    )}
                                </div>
                                <div className="col col-md-6">
                                    <label>Riser(s)</label>
                                    {yesNo((data as any)?.tank2Risers)}
                                </div>
                            </div>
                        </div>
                    </div>
                    {(data as any)?.tank2DepthToLidCompliance?.complianceValue === "Out of Compliance" ? (
                        <div className="row mb-3">
                            <label>{(data as any)?.tank2DepthToLidComment}</label>
                        </div>
                    ) : null}
                </SummaryCard>
            )}
        </>
    );
};

export default TankInstallationReadOnly;


